import { QueryState } from "../types/queryState";
import { useLocation, useNavigate } from "react-router-dom";
import { Buffer } from "buffer";
import { CLIMATE_LAYERS } from "../enums/layers";

const DEFAULT_QUERY_STATE = {
  country: "",
  aggregationLevel: "1",
  geographicalUnit: "",
  climate: CLIMATE_LAYERS[0].displayName,
  season: [],
  latitude: 4.941597145954475,
  longitude: 52.79204410016732,
  zoom: 5,
  layers: [],
  droughtEvents: false,
  ipcScore: false,
  queryId: "",
  year: 2010,
  ui: {
    timeseries: false,
    mainSidebar: true,
    mapSidebar: true,
    droughtEvents: false,
    ipcScore: false,
  },
  payout: {
    minPayout: "8.00",
    maxPayout: "57.1",
    deductible: "5.00",
    sumInsured: "100.0",
    exitLevel: 0.05,
    triggerLevel: 0.25,
    comparisonFocus: 0,
    geographicalAreas: [],
    seasons: {
      1: 50,
      2: 50,
    },
  },
};

const useQueryState = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const encodeQueryState = (params: QueryState) => {
    const jsonString = JSON.stringify(params);
    const base64Url = Buffer.from(jsonString)
      .toString("base64")
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");

    return base64Url;
  };

  const decodeQueryParams = (base64String: string) => {
    let base64 = base64String.replace(/-/g, "+").replace(/_/g, "/");

    while (base64.length % 4 !== 0) {
      base64 += "=";
    }

    return JSON.parse(Buffer.from(base64, "base64").toString("utf-8"));
  };

  const extractQueryState = (): QueryState => {
    const query = new URLSearchParams(search);
    const encodedParams = query.get("options");

    if (encodedParams) {
      const decodedParams = decodeQueryParams(encodedParams);

      try {
        if (decodedParams.layers && typeof decodedParams.layers === "string") {
          decodedParams.layers = JSON.parse(decodedParams.layers);
        }
      } catch (error) {
        console.error("Error parsing layers from query parameters:", error);
        decodedParams.layers = [];
      }
      return decodedParams;
    }

    return {
      ...DEFAULT_QUERY_STATE,
    };
  };

  const currentQueryState = extractQueryState();

  const setQueryState = (
    updater: Partial<QueryState> | ((prev: QueryState) => Partial<QueryState>),
  ) => {
    const currentState = extractQueryState();

    const partial =
      typeof updater === "function" ? updater(currentState) : updater;

    const updatedState: QueryState = {
      ...currentState,
      ...partial,
      ui: {
        ...currentState.ui,
        ...(partial.ui ?? {}),
      },
      payout: {
        ...currentState.payout,
        ...(partial.payout ?? {}),
      },
    };

    const encodedState = encodeQueryState(updatedState);
    const current = new URLSearchParams(search).get("options");

    if (current === encodedState) return;

    navigate(
      {
        pathname,
        search: `?options=${encodedState}`,
      },
      { replace: true },
    );
  };

  return {
    queryState: currentQueryState,
    setQueryState,
  };
};

export default useQueryState;
